import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function PercentYieldCalculator() {
  const [actualYield, setActualYield] = useState("");
  const [theoreticalYield, setTheoreticalYield] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const actual = parseFloat(actualYield);
    const theoretical = parseFloat(theoreticalYield);

    if (isNaN(actual) || isNaN(theoretical) || theoretical <= 0 || actual < 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const percentYield = (actual / theoretical) * 100;
    setResult(percentYield);

    toast({
      title: "Calculation Complete",
      description: `Percent Yield: ${percentYield.toFixed(2)}%`,
    });
  };

  const reset = () => {
    setActualYield("");
    setTheoreticalYield("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Percent Yield Calculator" 
      calculatorPath="/calculator/percent-yield"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Percent Yield Calculator</CardTitle>
            <CardDescription>
              Calculate percent yield from actual and theoretical yields
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="actual">Actual Yield (g)</Label>
                <Input
                  id="actual"
                  type="number"
                  step="any"
                  value={actualYield}
                  onChange={(e) => setActualYield(e.target.value)}
                  placeholder="Enter actual yield"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="theoretical">Theoretical Yield (g)</Label>
                <Input
                  id="theoretical"
                  type="number"
                  step="any"
                  value={theoreticalYield}
                  onChange={(e) => setTheoreticalYield(e.target.value)}
                  placeholder="Enter theoretical yield"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)}%</p>
                    <CopyResultButton value={`${result.toFixed(2)}%`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">% Yield = (Actual / Theoretical) × 100</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
